import { Button, Container, Element, Label, TextInput } from '@playcanvas/pcui';

import { Events } from '../events';
import { localize } from './localization';
import loginIcon from './svg/login.svg';
import { AuthService, User } from '../auth';

const createSvg = (svgString: string, args = {}) => {
    const decodedStr = decodeURIComponent(svgString.substring('data:image/svg+xml,'.length));
    return new Element({
        dom: new DOMParser().parseFromString(decodedStr, 'image/svg+xml').documentElement,
        ...args
    });
};

class LoginDialog extends Container {
    show: () => Promise<User | null>;
    hide: () => void;
    destroy: () => void;

    constructor(events: Events, authService: AuthService, args = {}) {
        args = {
            ...args,
            id: 'login-dialog',
            class: 'settings-dialog',
            hidden: true,
            tabIndex: -1
        };

        super(args);

        const dialog = new Container({
            id: 'dialog'
        });

        // header
        const headerIcon = createSvg(loginIcon, { id: 'icon' });
        const headerText = new Label({ id: 'text', text: localize('user.login') });
        const header = new Container({ id: 'header' });
        header.append(headerIcon);
        header.append(headerText);

        // email
        const emailLabel = new Label({ class: 'label', text: localize('user.email') });
        const emailInput = new TextInput({ class: 'text-input' });
        const emailRow = new Container({ class: 'row' });
        emailRow.append(emailLabel);
        emailRow.append(emailInput);

        // password
        const passwordLabel = new Label({ class: 'label', text: localize('user.password') });
        const passwordInput = new TextInput({ class: 'text-input' });
        // Set input type to password
        (passwordInput.dom.querySelector('input') as HTMLInputElement).type = 'password';
        const passwordRow = new Container({ class: 'row' });
        passwordRow.append(passwordLabel);
        passwordRow.append(passwordInput);

        // error message
        const errorLabel = new Label({ id: 'error-label', text: '', hidden: true });

        // content
        const content = new Container({ id: 'content' });
        content.append(emailRow);
        content.append(passwordRow);
        content.append(errorLabel);

        // footer
        const footer = new Container({ id: 'footer' });

        const cancelButton = new Button({
            class: 'button',
            text: localize('render.cancel')
        });

        const loginButton = new Button({
            class: 'button',
            text: localize('user.login')
        });

        footer.append(cancelButton);
        footer.append(loginButton);

        dialog.append(header);
        dialog.append(content);
        dialog.append(footer);

        this.append(dialog);

        // handle key bindings for enter and escape
        let onCancel: () => void;
        let onOK: () => void;

        cancelButton.on('click', () => onCancel());
        loginButton.on('click', () => onOK());

        const keydown = (e: KeyboardEvent) => {
            switch (e.key) {
                case 'Escape':
                    onCancel();
                    break;
                case 'Enter':
                    if (!e.shiftKey) onOK();
                    break;
                default:
                    e.stopPropagation();
                    break;
            }
        };

        const reset = () => {
            emailInput.value = '';
            passwordInput.value = '';
            errorLabel.text = '';
            errorLabel.hidden = true;
            loginButton.enabled = true;
            emailInput.enabled = true;
            passwordInput.enabled = true;
        };

        this.show = () => {
            reset();

            this.hidden = false;
            this.dom.addEventListener('keydown', keydown);
            // focus email input on show
            setTimeout(() => emailInput.focus(), 0);

            return new Promise<User | null>((resolve) => {
                onCancel = () => {
                    resolve(null);
                };

                onOK = async () => {
                    const email = emailInput.value;
                    const password = passwordInput.value;

                    if (!email || !password) {
                        errorLabel.text = localize('user.invalid-credentials');
                        errorLabel.hidden = false;
                        return;
                    }

                    loginButton.enabled = false;
                    emailInput.enabled = false;
                    passwordInput.enabled = false;
                    errorLabel.hidden = true;

                    const user = await authService.login(email, password);

                    if (user) {
                        resolve(user);
                    } else {
                        errorLabel.text = localize('user.invalid-credentials');
                        errorLabel.hidden = false;
                        loginButton.enabled = true;
                        emailInput.enabled = true;
                        passwordInput.enabled = true;
                    }
                };
            }).finally(() => {
                this.dom.removeEventListener('keydown', keydown);
                this.hide();
            });
        };

        this.hide = () => {
            this.hidden = true;
        };

        this.destroy = () => {
            this.hide();
            super.destroy();
        };
    }
}

export { LoginDialog };
